import {ChangeDetectorRef, Component, OnInit} from '@angular/core';

import {TranslateService} from '@ngx-translate/core';

import {BehaviorSubject, merge, Observable, of} from 'rxjs';
import {catchError, filter, finalize, map, share, switchMap, takeUntil, tap} from 'rxjs/operators';

import {ToastrService} from 'ngx-toastr';
import {Destroyable} from '../../../../destroyable.component';
import {LocalizationService} from '../../../../shared/localization/localization.service';
import {ProcessInstancesResponse, Request} from '../../../../shared/workflow/workflow-v2.interface';
import {requests_per_page, WorkflowV2Service} from '../../../../shared/workflow/workflow-v2.service';
import {RequestService} from '../../services/request.service';

@Component({
  selector: 'gp-requests-in-progress',
  templateUrl: './requests-in-progress.component.html',
  styleUrls: ['../../workflows-common.component.scss']
})
export class RequestsInProgressComponent extends Destroyable implements OnInit {

  public size = requests_per_page;

  public trigger$: Observable<number>;
  public requests: Request[] = [];
  private canceledRequestSubject = new BehaviorSubject<Request>(null);
  public loading: boolean;
  public total: number;

  private currentPage = 0;
  private loadMore$ = new BehaviorSubject<number>(null);

  constructor(private workflowService: WorkflowV2Service,
              private cd: ChangeDetectorRef,
              private requestService: RequestService,
              public translate: TranslateService,
              private toastrService: ToastrService,
              private localization: LocalizationService) {
    super();
    this.trigger$ = merge(
      this.localization.changeDateFormatEvent,
      this.localization.changeLanguageEvent
    ).pipe(
      map(() => Math.random()),
      share(),
      takeUntil(this.componentDestroyed$)
    );
  }

  ngOnInit() {
    this.requestService.getCancelRequestObservable().pipe(
      tap(() => {
        this.currentPage = 0;
        this.requests = [];
        this.loadMore$.next(this.currentPage);
      }),
      takeUntil(this.componentDestroyed$))
      .subscribe();

    this.loadMore$.pipe(
      filter(value => value !== null),
      switchMap((page: number) => {
        this.loading = true;
        return this.workflowService.getRequests({'per-page': this.size, page})
          .pipe(
            catchError(() => of({data: [], total: 0})),
            tap((response: ProcessInstancesResponse) => {
              this.total = response.total;
              this.requests = [...this.requests, ...response.data];
            }),
            finalize(() => {
              this.loading = false;
              this.cd.detectChanges();
            })
          );
      }),
      takeUntil(this.componentDestroyed$)
    ).subscribe();

    this.canceledRequestSubject.pipe(
      filter((request) => request !== null),
      switchMap((request) => this.workflowService.cancelRequest(request)),
      tap(() => {
        this.toastrService.success(this.translate.instant('WORKFLOWS.request_canceled'));
        this.requestService.cancelRequest();
        this.cd.detectChanges();
      }),
      takeUntil(this.componentDestroyed$)).subscribe();
  }

  public onCancelRequest(request: Request): void {
    this.canceledRequestSubject.next(request);
  }

  public loadMore() {
    if (!this.loading) {
      this.currentPage = this.currentPage + 1;
      this.loadMore$.next(this.currentPage);
    }
  }
}
